import { useState, useEffect, useCallback } from "react";
import api from "@/lib/api";
import { ChannelIcon } from "@/components/ui-bits";
import { rc } from "@/lib/connect";
import { ArrowUpRight, ArrowDownLeft, ChatCircleDots } from "@phosphor-icons/react";

const STATUS_TONE = {
  sent: "bg-blue-100 text-blue-800",
  delivered: "bg-emerald-100 text-emerald-800",
  read: "bg-teal-100 text-teal-800",
  opened: "bg-teal-100 text-teal-800",
  replied: "bg-violet-100 text-violet-800",
  answered: "bg-emerald-100 text-emerald-800",
  missed: "bg-amber-100 text-amber-800",
  scheduled: "bg-violet-100 text-violet-800",
  pending: "bg-amber-100 text-amber-800",
  failed: "bg-red-100 text-red-800",
};

function fmtWhen(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
}

// Reusable unified communication timeline. Pass `accountId` to scope to one account
// (for embedding in Account 360), or omit for a tenant-wide feed. Built on the Phase 1A
// `communications` collection. Never renders fabricated/demo rows.
export function CommunicationTimeline({ accountId = null, limit = 50, compact = false, className = "",
  emptyTitle = "No communications yet.",
  emptyText = "Your customer communications will appear here once you start using RecoverOS Connect.",
  emptyActions = null }) {
  const [items, setItems] = useState(null);

  const load = useCallback(() => {
    const params = { limit };
    if (accountId) params.account_id = accountId;
    api.get("/connect/communications", { params })
      .then((r) => setItems(r.data?.items || []))
      .catch(() => setItems([]));
  }, [accountId, limit]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const h = () => load();
    window.addEventListener("recoveros:comms-changed", h);
    return () => window.removeEventListener("recoveros:comms-changed", h);
  }, [load]);

  if (items === null) {
    return <div className="text-sm text-muted-foreground py-8 text-center" data-testid="comm-timeline-loading">Loading communications…</div>;
  }

  if (items.length === 0) {
    return (
      <div className="text-center py-12 border border-dashed border-border rounded-sm px-4" data-testid="comm-timeline-empty">
        <ChatCircleDots size={28} weight="duotone" className="mx-auto text-muted-foreground/60" />
        <div className="mt-2 text-sm font-semibold">{emptyTitle}</div>
        <div className="text-xs text-muted-foreground mt-1 max-w-sm mx-auto">{emptyText}</div>
        {emptyActions && <div className="mt-4 flex flex-wrap items-center justify-center gap-2">{emptyActions}</div>}
      </div>
    );
  }

  return (
    <ol className={`relative border-l border-border ml-2 ${className}`} data-testid="comm-timeline">
      {items.map((c) => {
        const DirIcon = c.direction === "in" ? ArrowDownLeft : ArrowUpRight;
        const tone = STATUS_TONE[c.status] || "bg-secondary text-secondary-foreground";
        return (
          <li key={c.id} className="mb-5 ml-5" data-testid={`comm-timeline-item-${c.id}`}>
            <span className="absolute -left-[9px] flex items-center justify-center w-4 h-4 rounded-full bg-card border border-border">
              <ChannelIcon channel={c.channel === "voice" ? "ivr" : c.channel} size={10} />
            </span>
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-xs font-semibold capitalize">{c.channel}</span>
              <DirIcon size={12} weight="bold" className={c.direction === "in" ? "text-emerald-600" : "text-blue-600"} />
              <span className={`inline-block px-2 py-0.5 text-[11px] font-semibold rounded-sm capitalize ${tone}`} data-testid={`comm-status-${c.id}`}>
                {(c.status || "").replace(/_/g, " ")}
              </span>
              <span className="text-[11px] text-muted-foreground ml-auto mono">{fmtWhen(c.created_at)}</span>
            </div>
            {!compact && (
              <div className="mt-1 text-xs text-muted-foreground line-clamp-2">
                {c.borrower_name ? <span className="font-medium text-foreground">{c.borrower_name}</span> : null}
                {c.borrower_name && (c.body || c.credits_charged_milli) ? " · " : ""}
                {c.body ? c.body : ""}
              </div>
            )}
            {c.credits_charged_milli > 0 && (
              <div className="mt-0.5 text-[11px] text-muted-foreground mono" data-testid={`comm-credits-${c.id}`}>
                {rc(c.credits_charged_milli)} used
              </div>
            )}
          </li>
        );
      })}
    </ol>
  );
}
